import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import displayError from '../../utils/ErrorHandler';
import Toast from './Toast';
import UserContext from '../../contexts/UserContext';
import { isAdminRole } from '../../utils/roles';

const CommitModal = ({ isOpen, onClose, item, commitType = 'item' }) => {
  const [formData, setFormData] = useState({
    jamiat: '',
    jamaat: '',
    individualName: '',
    volunteerName: '',
    quantity: '',
    inchargeName: '',
    contact: '',
    email: '',
    estimatedDeliveryDate: '',
    contributionType: 'kind'
  });

  const [jamiats, setJamiats] = useState([]);
  const [jamaats, setJamaats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [totalPrice, setTotalPrice] = useState(0);
  const [toast, setToast] = useState({ message: "", type: "success" });
  const [isPersonal, setIsPersonal] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const { user } = useContext(UserContext);

  const isZabihatCommit = commitType === 'zabihat' || item?.isZabihat;
  // The "individual/personal commitment" option is only meant for admins
  // committing on someone's behalf from the admin portal — the Umaal ('Silat'
  // role) committing screen must always require a Jamiat/Jamaat.
  const canCommitPersonal = isAdminRole(user?.role);

  useEffect(() => {
    if (isOpen) {
      fetchJamiats();
      setFormData({
        jamiat: '',
        jamaat: '',
        individualName: '',
        volunteerName: '',
        quantity: '',
        inchargeName: '',
        contact: '',
        email: '',
        estimatedDeliveryDate: '',
        contributionType: 'kind'
      });
      setIsPersonal(false);
      setTotalPrice(0);
      setError('');
      setFieldErrors({});
      setJamaats([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (formData.quantity && item?.price) {
      setTotalPrice(parseFloat(formData.quantity) * parseFloat(item.price));
    } else {
      setTotalPrice(0);
    }
  }, [formData.quantity, item?.price]);

  useEffect(() => {
    if (formData.jamiat && !isPersonal) {
      fetchJamaats(formData.jamiat);
      setFormData(prev => ({ ...prev, jamaat: '' }));
    } else {
      setJamaats([]);
    }
  }, [formData.jamiat, isPersonal]);

  const fetchJamiats = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URI}/api/jamiat/get`, { withCredentials: true });
      if (res.data.success && res.data.data.jamiat_name) {
        const jamiatNames = res.data.data.jamiat_name;
        const filtered = jamiatNames.filter(j => j === user?.Jamiat);
        if (user.Mauze) {
          setJamiats(filtered);
        } else {
          setJamiats(jamiatNames);
        }
        setFormData(prev => ({ ...prev, jamiat: filtered[0] }));

        if (commitType === 'zabihat') {
          item.price = res.data.data.zabihat.price;
          item.itemId = res.data.data.zabihat._id;
          item.unit = res.data.data.zabihat.unit;
        }
      }
    } catch (err) {
      setError(displayError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchJamaats = async (jamiatName) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URI}/api/jamiat/getAll`,
        { jamiat_name: jamiatName },
        { withCredentials: true }
      );
      if (res.data.success && res.data.data) {
        const data = res.data.data;
        const filtered = data.filter(j => j.jamaat_name === user?.Mauze);
        setJamaats(user.Mauze ? filtered : data);
        setFormData(prev => ({ ...prev, jamaat: data[0]._id }));
      }
    } catch (err) {
      setError(displayError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'isPersonal') {
      setIsPersonal(checked);
      if (checked) {
        setFormData(prev => ({ ...prev, jamiat: '', jamaat: '', individualName: '' }));
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setFieldErrors(prev => ({ ...prev, [name]: "" })); // clear field error on change
  };

  const validateForm = () => {
    const errors = {};

    if (isPersonal) {
      if (!formData.individualName.trim()) {
        errors.individualName = "Please enter your name";
      }
    } else {
      if (!formData.jamiat) errors.jamiat = "Please select a Jamiat";
      if (!formData.jamaat) errors.jamaat = "Please select a Jamaat";
    }

    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      errors.quantity = "Quantity must be greater than 0";
    } else if (typeof item?.pendingQuantity === 'number' && parseFloat(formData.quantity) > item.pendingQuantity) {
      // Mirrors the backend's own required/pending quantity check (see createCommitment) —
      // catch it client-side so the user gets an inline reason instead of a submit-time 400.
      errors.quantity = `Quantity cannot exceed the pending quantity (${item.pendingQuantity}${item.unit ? ' ' + item.unit : ''})`;
    }

    if (!formData.inchargeName.trim()) {
      errors.inchargeName = "Please enter Incharge Name";
    }

    // const phoneRegex = /^[0-9]{10}$/;
    // if (!phoneRegex.test(formData.contact)) {
    //   errors.contact = "Please enter a valid 10-digit contact number";
    // }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.estimatedDeliveryDate) {
      errors.estimatedDeliveryDate = "Please select a date";
    } else {
      const selectedDate = new Date(formData.estimatedDeliveryDate);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      if (selectedDate < tomorrow) {
        errors.estimatedDeliveryDate = "Date must be tomorrow or later";
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    if (!validateForm()) {
      setToast({ message: "Please fix the errors before submitting", type: "error" });
      setSubmitting(false);
      return;
    }

    try {
      let submitData = {
        itemId: isZabihatCommit ? item.itemId : item._id,
        quantity: parseFloat(formData.quantity),
        totalPrice,
        inchargeName: formData.inchargeName,
        contact: formData.contact,
        email: formData.email,
        estimatedDeliveryDate: formData.estimatedDeliveryDate,
        personalCommitment: isPersonal
      };

      if (isPersonal) {
        submitData.individualName = formData.individualName;
      } else {
        submitData.jamaat = formData.jamaat;
        if (formData.volunteerName.trim()) {
          submitData.volunteerName = formData.volunteerName.trim();
        }
      }

      if (isZabihatCommit) {
        submitData.pricePerUnit = item.price;
      } else {
        submitData.contributionType = formData.contributionType;
      }

      const endpoint = isZabihatCommit
        ? `${process.env.REACT_APP_API_URI}/api/commit/zabihat-commit/add`
        : `${process.env.REACT_APP_API_URI}/api/commit/add`;

      await axios.post(endpoint, submitData, { withCredentials: true });

      setToast({
        message: `${isZabihatCommit ? "Zabihat" : item.name} commitment submitted successfully`,
        type: "success",
      });
    } catch (err) {
      // Surface the backend's actual reason (e.g. "Quantity cannot be greater than
      // required/pending quantity!") instead of axios's generic "Request failed with
      // status code 400" — the latter was what Umaal users were seeing, with no clue why.
      setToast({
        message: err.response?.data?.custommessage || err.response?.data?.message || err.message || "Something went wrong",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (!isOpen || !item) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className={`${isZabihatCommit ? 'bg-green-600' : 'bg-fmb'} text-white p-4 rounded-t-lg`}>
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold">
              {isZabihatCommit ? 'Commit Zabihat' : `Commit ${item.name}`}
            </h2>
            <button onClick={onClose} className="text-white text-2xl font-bold">×</button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">

          {/* Checkbox for personal commitment — admin portal only */}
          {canCommitPersonal && (
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isPersonal"
                name="isPersonal"
                checked={isPersonal}
                onChange={handleInputChange}
                className="w-5 h-5 text-green-600"
              />
              <label htmlFor="isPersonal" className="text-sm text-gray-700">
                This is an individual/personal commitment (not on behalf of a Jamaat)
              </label>
            </div>
          )}

          {/* Show Jamiat/Jamaat selectors only if NOT personal */}
          {!isPersonal && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-fmb">Location Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Jamiat</label>
                  <select
                    name="jamiat"
                    value={formData.jamiat}
                    onChange={handleInputChange}
                    disabled={loading}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select Jamiat</option>
                    {jamiats.map((j, idx) => (
                      <option key={idx} value={j}>{j}</option>
                    ))}
                  </select>
                  {fieldErrors.jamiat && <p className="text-red-500 text-sm">{fieldErrors.jamiat}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Jamaat</label>
                  <select
                    name="jamaat"
                    value={formData.jamaat}
                    onChange={handleInputChange}
                    disabled={loading || !formData.jamiat}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg"
                  >
                    <option value="">Select Jamaat</option>
                    {jamaats.map(j => (
                      <option key={j._id} value={j._id}>
                        {j.jamaat_name || j.name}
                      </option>
                    ))}
                  </select>
                  {fieldErrors.jamaat && <p className="text-red-500 text-sm">{fieldErrors.jamaat}</p>}
                </div>
              </div>
              <div>
                <label htmlFor="volunteerName" className="block text-sm font-medium text-gray-700 mb-2">
                  Name (If some particular person from Jamiat is contributing)
                </label>
                <input
                  type="text"
                  name="volunteerName"
                  id="volunteerName"
                  value={formData.volunteerName}
                  onChange={handleInputChange}
                  placeholder="Optional"
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb focus:border-fmb"
                />
              </div>
            </div>
          )}

          {/* Show individualName textbox only if personal */}
          {isPersonal && (
            <div>
              <label htmlFor="individualName" className="block text-sm font-medium text-gray-700 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="individualName"
                id="individualName"
                value={formData.individualName}
                onChange={handleInputChange}
                required={isPersonal}
                placeholder="Enter your name"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              {fieldErrors.individualName && <p className="text-red-500 text-sm">{fieldErrors.individualName}</p>}
            </div>
          )}


        {!isZabihatCommit && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">How will you contribute?</label>
            <div className="flex gap-6">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="contributionType"
                  value="kind"
                  checked={formData.contributionType === 'kind'}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-green-600"
                />
                In Kind (provide the item itself)
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="contributionType"
                  value="cash"
                  checked={formData.contributionType === 'cash'}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-green-600"
                />
                Price (pay item's value for the quantity)
              </label>
            </div>
          </div>
        )}

        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">{`Quantity`}</label>
            <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-1 rounded-lg">
              Pending: {item.pendingQuantity} {item.unit}
            </span>
          </div>
          <input
            type="number"
            name="quantity"
            value={formData.quantity}
            onChange={handleInputChange}
            onWheel={(e) => e.target.blur()}
            required
            className="w-full p-3 border border-gray-300 rounded-lg"
            placeholder="Enter quantity"
            min="0.01"
            step="0.01"
          />
          {fieldErrors.quantity && <p className="text-red-500 text-sm">{fieldErrors.quantity}</p>}
        </div>


          {/* Total Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {!isZabihatCommit && formData.contributionType === 'cash' ? 'Amount Payable' : 'Total Price'}
            </label>
            <div className="p-3 bg-gray-100 border border-gray-300 rounded-lg">
              ₹{totalPrice.toFixed(2)}
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Incharge Name</label>
              <input
                type="text"
                name="inchargeName"
                value={formData.inchargeName}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg"
              />
              {fieldErrors.inchargeName && <p className="text-red-500 text-sm">{fieldErrors.inchargeName}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact</label>
              <input
                type="tel"
                name="contact"
                value={formData.contact}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg"
              />
              {fieldErrors.contact && <p className="text-red-500 text-sm">{fieldErrors.contact}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg"
              />
              {fieldErrors.email && <p className="text-red-500 text-sm">{fieldErrors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isZabihatCommit ? 'Estimated Payment Date' : 'Estimated Delivery Date'}
              </label>
              <input
                type="date"
                name="estimatedDeliveryDate"
                value={formData.estimatedDeliveryDate}
                onChange={handleInputChange}
                required
                min={getTomorrowDate()}
                className="w-full p-3 border border-gray-300 rounded-lg"
              />
              {fieldErrors.estimatedDeliveryDate && <p className="text-red-500 text-sm">{fieldErrors.estimatedDeliveryDate}</p>}
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-600 rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-green-600 text-white rounded-lg"
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>

      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => {
            setToast({ message: "", type: "success" });
            onClose();
          }}
        />
      )}
    </div>
  );
};

export default CommitModal;